import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ViewportScroller } from '@angular/common';
import { BehaviorSubject, Subject, Subscription, from } from 'rxjs';
import { filter, first, timeout } from 'rxjs/operators';
import { ToastService } from 'ngx-zcs-components';
import gql from 'graphql-tag';
import {
  CommunicationChannel,
  TypeInternetDevice,
  ModbusLink,
  INTERNET_RETRY_POLICY,
  encodeMultiQuery,
  decodeMultiQuery,
  fromHexStringToUint8Array,
  estimateModbusResponseSize,
  splitModbusFrames,
} from 'ngx-modbus';
import {
  AHdeviceTypeEnum,
  AzzurroHubModbusRes,
  ReceiveAckResponse,
  SendModbusCommandForAHMutation,
  OnReceiveAckSubscription,
  OnReceiveModbuscommandForAHSubscription,
  generateTransactionId,
} from 'ngx-core-services';

import { resolveInverterVersion } from './utils';
import { blockServiceLabels } from '../block-translations.service';

const AH_DEBUG = { ENABLED: true };
const ahLog = (msg: string) => AH_DEBUG.ENABLED && console.log(`[Modbus:AH] ${msg}`);

const sendCommandFragment = gql`
  fragment sendcommandstringresponse on SendCommandStringResponse {
    collectionTime
    orderId
    success
    code
    msg
    connectStatus
  }
`;

const receiveAckResponseQuery = gql`
  fragment receiveackresponse on ReceiveAckResponse {
    transactionId
    version
    deviceId
    timestamp
    dataType
    message
    success
  }
`;

const subscriptionFragment = gql`
  fragment azzurrohubmodbusres on AzzurroHubModbusRes {
    id
    version
    deviceSN
    gatewaySN
    deviceType
    timestamp
    dataType
    dataMap {
      groupID
      result
      commandResponse
    }
  }
`;

@Injectable({
  providedIn: 'root',
})
export class InternetAHService implements CommunicationChannel {
  readonly refreshByDefault = 20 * 60 * 1000;
  readonly maxTimeoutInMs = 20 * 1000;
  readonly delayBetweenCommandsInMs = 1;

  readonly connectionStatus$ = new BehaviorSubject<boolean>(false);
  readonly response$ = new Subject<Uint8Array>();
  readonly responseByAppsync$ = new Subject<AzzurroHubModbusRes>();

  readonly concurrentQuery = {
    count: 512, // AH: larger buffer to reduce round-trips through cloud gateway
    encodeMultiQuery,
    decodeMultiQuery,
  };

  command?: ModbusLink;
  loggerId?: number;
  deviceId?: string;
  inverterId?: string;

  private gatewaySN?: string;
  private _deviceSN?: string;
  private _deviceType: AHdeviceTypeEnum = AHdeviceTypeEnum.inverter;
  private _modbusAddress = 1;
  private responseSubscription$?: Subscription;
  private ackSubscription$?: Subscription;
  private internetDevice?: TypeInternetDevice;

  get isConnected(): boolean {
    return !!this.command;
  }

  constructor(
    private readonly sendModbusCommandForAHMutation: SendModbusCommandForAHMutation,
    private readonly onReceiveAckSubscription: OnReceiveAckSubscription,
    private readonly onReceiveModbuscommandForAHSubscription: OnReceiveModbuscommandForAHSubscription,
    private readonly router: Router,
    private readonly toastService: ToastService,
    private readonly viewportScroller: ViewportScroller,
  ) {
    this.interaction = this.interaction.bind(this);
    this.close = this.close.bind(this);
  }

  readonly scan = async () => { throw new Error('Method not implemented.'); };

  async open(device: TypeInternetDevice): Promise<void> {
    this.internetDevice = device;

    // Clean up previous connection
    if (this.responseSubscription$) {
      this.responseSubscription$.unsubscribe();
    }
    if (this.ackSubscription$) {
      this.ackSubscription$.unsubscribe();
    }
    this.command?.destroy();

    // Extract AH-specific params from device
    this.gatewaySN = device.gatewaySN;
    this._deviceSN = device.serial;
    this._deviceType = (device.deviceType as AHdeviceTypeEnum) ?? AHdeviceTypeEnum.inverter;
    this._modbusAddress = device.modbusAddress ?? 1;

    this.deviceId = this._deviceSN;
    this.inverterId = this._deviceSN;

    if (!this.gatewaySN || !this._deviceSN) {
      throw new Error(blockServiceLabels.ahChannelConnectionError);
    }

    ahLog(`open(gatewaySN="${this.gatewaySN}", deviceSN="${this._deviceSN}", modbusAdr=${this._modbusAddress})`);

    // Subscribe to AppSync responses for this gateway
    const responseInner$ = await this.onReceiveModbuscommandForAHSubscription.execute(
      subscriptionFragment,
      { gatewaySN: this.gatewaySN },
    ).pipe(first()).toPromise();
    this.responseSubscription$ = from(responseInner$ as any).subscribe((result: any) => {
      const res = result?.data?.onReceiveModbuscommandForAH as AzzurroHubModbusRes;
      if (res) {
        this.responseByAppsync$.next(res);
      }
    });

    const ackInner$ = await this.onReceiveAckSubscription.execute(
      receiveAckResponseQuery,
      { deviceId: this.gatewaySN },
    ).pipe(first()).toPromise();
    this.ackSubscription$ = from(ackInner$ as any).subscribe((result: any) => {
      const ack = result?.data?.onReceiveAck as Partial<ReceiveAckResponse>;
      if (ack) {
        ahLog(`[ACK-PROBE] txId=${ack.transactionId}, success=${ack.success}, dataType=${ack.dataType}, message=${ack.message ?? 'none'}, ts=${ack.timestamp}`);
      }
    });

    ahLog(`AppSync subscriptions active for gatewaySN="${this.gatewaySN}" (modbus + ACK-probe)`);

    const handleFatalError = async () => {
      ahLog(`Fatal error — navigating to home`);
      await this.close();
      await this.toastService.persistence(
        blockServiceLabels.ahChannelConnectionError,
        'Error',
      );

      if (this.internetDevice?.onFatalError) {
        this.internetDevice.onFatalError();
        return;
      }

      await this.router.navigate(['/']).then(() => {
        this.viewportScroller.scrollToPosition([0, 0]);
      });
    };

    const inverterVersion = resolveInverterVersion(this._deviceSN);

    this.command = new ModbusLink({
      deviceId: this._deviceSN,
      inverterVersion,
      concurrentQuery: this.concurrentQuery,
      executeCmd: this.interaction,
      modbusAddress: this._modbusAddress,
      maxTimeoutInMs: this.maxTimeoutInMs,
      handleFatalError,
      delayBetweenCommandsInMs: this.delayBetweenCommandsInMs,
      retryPolicy: INTERNET_RETRY_POLICY,
    });

    this.connectionStatus$.next(true);
  }

  async interaction(cmd: Uint8Array): Promise<Uint8Array> {
    if (!this._deviceSN || !this.gatewaySN) {
      throw new Error(blockServiceLabels.deviceNotFoundError);
    }

    // Split concatenated multi-query buffer into individual Modbus frames
    const frames = splitModbusFrames(cmd);
    const dataMap = frames.map((frame) => {
      const commandString = Array.from(frame).map(x => x.toString(16).padStart(2, '0')).join('');
      const expectedRetByte = estimateModbusResponseSize(frame);
      return { commandString, expectedRetByte };
    });

    ahLog(`SEND - ${dataMap.length} frame(s) to ${this.gatewaySN}/${this._deviceSN}`);
    dataMap.forEach((f, i) => ahLog(`  TX[${i}] hex=${f.commandString}, expectedRetByte=${f.expectedRetByte}`));

    const transactionId = generateTransactionId();
    const result = await this.sendModbusCommandForAHMutation.execute(sendCommandFragment, {
      loggerId: this.gatewaySN,
      deviceSN: this._deviceSN,
      deviceType: this._deviceType,
      transactionId,
      dataMap,
    });

    const mutationResponse = result.data?.sendModbusCommandForAH;

    if (mutationResponse?.success === false) {
      ahLog(`Mutation failed: ${JSON.stringify(mutationResponse)}`);
      throw new Error(blockServiceLabels.requestFailedError);
    }

    const orderId = mutationResponse?.orderId;
    ahLog(`Mutation OK — orderId=${orderId}, txId=${transactionId}, waiting for modbus response...`);

    // Wait for subscription response matching this orderId (with timeout to prevent hangs)
    const subscriptionResponse = await this.responseByAppsync$.pipe(
      filter(r => r.id === String(orderId)),
      first(),
      timeout(this.maxTimeoutInMs),
    ).toPromise();

    // Reassemble: concatenate all commandResponse values from dataMap[] in order
    const responseDataMap = subscriptionResponse?.dataMap ?? [];
    const responseHexParts: string[] = [];

    for (let i = 0; i < responseDataMap.length; i++) {
      const entry = responseDataMap[i];
      ahLog(`  RX[${i}] commandResponse=${entry.commandResponse ?? '(empty)'}, result=${entry.result}`);
      if (!entry.commandResponse) {
        ahLog(`Empty commandResponse at dataMap[${i}] (orderId=${orderId}, result=${entry.result})`);
        throw new Error(blockServiceLabels.requestFailedError);
      }
      responseHexParts.push(entry.commandResponse);
    }

    // Concatenate all response frames into a single buffer (matching encodeMultiQuery behavior)
    const concatenatedHex = responseHexParts.join('');
    const responseAsUint = fromHexStringToUint8Array(concatenatedHex);

    ahLog(`RECEIVE - 0x${Array.from(responseAsUint).map(x => x.toString(16).padStart(2, '0')).join(' ')}`);

    this.response$.next(responseAsUint);
    return responseAsUint;
  }

  async close(): Promise<void> {
    if (this.responseSubscription$) {
      this.responseSubscription$.unsubscribe();
      this.responseSubscription$ = undefined;
    }
    if (this.ackSubscription$) {
      this.ackSubscription$.unsubscribe();
      this.ackSubscription$ = undefined;
    }
    this.command?.destroy();
    this.command = undefined;
    this.connectionStatus$.next(false);

    ahLog('close() — subscription unsubscribed, ModbusLink destroyed');
  }
}
